<template>
  <div class="tasks-tab">
    <div class="tasks-tab__bordered-container">
      <!-- Sub-tabs -->
      <Tab
        class="tasks-tab__sub-tabs-wrapper"
        type="underline"
        :modelValue="filter"
        :tabs="subTabs"
        @change="changeFilter"
      />

      <!-- Loading -->
      <div v-if="loading || initLoading" class="tasks-tab__loading">
        <Loading :loading="true" loadingText="Loading tasks..." />
      </div>

      <!-- Error -->
      <div v-else-if="error" class="tasks-tab__error">
        <div class="tasks-tab__error-box">
          <span>{{ error }}</span>
          <button class="tasks-tab__retry" @click="retry">Retry</button>
        </div>
      </div>

      <!-- Empty -->
      <div v-else-if="items.length === 0" class="tasks-tab__empty">
        <p>No tasks yet</p>
      </div>

      <!-- Content -->
      <template v-else>
        <div class="tasks-tab__content-area">
        <!-- Title + Content wrapper -->
        <div class="tasks-tab__title-content">
          <!-- Header row -->
          <div class="tasks-tab__header-row">
            <div class="tasks-tab__header-left">
              <div class="tasks-tab__header-checkbox-spacer" />
              <div class="tasks-tab__header-title-desc">
                <div class="tasks-tab__header-title">TASK</div>
                <div class="tasks-tab__header-patient">PATIENT NAME</div>
                <div class="tasks-tab__header-desc">DESCRIPTION</div>
              </div>
            </div>
            <div class="tasks-tab__header-date">DUE DATE</div>
            <div class="tasks-tab__header-actions-spacer" />
          </div>

          <!-- Task rows container -->
          <div class="tasks-tab__rows-container">
            <div
              v-for="(task, index) in items"
              :key="getTaskRowKey(task, index)"
              :class="['tasks-tab__row', { 'tasks-tab__row--completed': task.completed, 'tasks-tab__row--overdue': task.overdue }]"
              @mouseenter="hoveredId = getTaskRowKey(task, index)"
              @mouseleave="hoveredId = null"
            >
              <!-- Left part: checkbox + title + description -->
              <div class="tasks-tab__row-left">
                <div
                  :class="['tasks-tab__circle', { 'tasks-tab__circle--checked': task.completed }]"
                  @click="toggleComplete(task)"
                >
                  <svg v-if="task.completed" width="18" height="18" viewBox="0 0 18 18" fill="none">
                    <path d="M6.75 12.1275L3.6225 9L2.5575 10.0575L6.75 14.25L15.75 5.25L14.6925 4.1925L6.75 12.1275Z" fill="#50C5CE"/>
                  </svg>
                </div>
                <div class="tasks-tab__title-desc-row">
                  <div :class="['tasks-tab__task-title', { 'tasks-tab__text--muted': task.completed }]">
                    {{ task.task }}
                  </div>
                  <div :class="['tasks-tab__patient-cell', { 'tasks-tab__text--muted': task.completed }]">
                    <template v-if="getPatientName(task)">
                      <svg width="18" height="17" viewBox="0 0 18 17" fill="none" xmlns="http://www.w3.org/2000/svg" class="tasks-tab__patient-icon">
                        <path d="M5.09091 3.86855C5.09091 1.73201 6.80037 0 8.90909 0C11.0178 0 12.7273 1.73201 12.7273 3.86855C12.7273 6.00509 11.0178 7.73709 8.90909 7.73709C6.80037 7.73709 5.09091 6.00509 5.09091 3.86855Z" :fill="task.completed ? '#999' : task.overdue ? '#666' : '#004879'"/>
                        <path d="M0 13.6689C0 11.5323 1.70946 9.80032 3.81818 9.80032H4.16525C4.3531 9.80032 4.53976 9.83041 4.71833 9.88949L5.59958 10.181C6.57856 10.5049 7.60772 10.6158 8.62107 10.5138C8.78414 10.4974 8.90945 10.669 8.84772 10.8228C8.55904 11.5421 8.4 12.3288 8.4 13.1531C8.4 14.4449 8.79058 15.6442 9.45861 16.6366C9.55188 16.7751 9.46224 16.9697 9.29683 16.9755C6.62937 17.0686 3.95513 16.8966 1.31271 16.4595C0.555809 16.3343 0 15.6718 0 14.8947V13.6689Z" :fill="task.completed ? '#999' : task.overdue ? '#666' : '#004879'"/>
                        <path d="M17.5 12.2101C17.5 14.7488 13.7848 16.8038 13.6266 16.8886C13.5849 16.9114 13.5383 16.9233 13.4909 16.9233C13.4436 16.9233 13.3969 16.9114 13.3552 16.8886C13.2353 16.8233 11.0804 15.6305 10.0377 13.9549C10.0243 13.933 10.0168 13.9078 10.0162 13.882C10.0156 13.8561 10.0218 13.8306 10.0342 13.808C10.0465 13.7854 10.0646 13.7666 10.0866 13.7535C10.1085 13.7403 10.1336 13.7334 10.1591 13.7333H11.4864C11.5335 13.7334 11.58 13.7216 11.6215 13.6991C11.6631 13.6766 11.6986 13.644 11.7248 13.6042L12.0591 13.0965L12.9661 14.4747C13.0009 14.5275 13.0518 14.5673 13.111 14.588C13.1702 14.6087 13.2345 14.6091 13.294 14.5893C13.3572 14.5676 13.4112 14.5249 13.4472 14.4681L13.9305 13.7333H14.6364C14.6756 13.7334 14.7145 13.7253 14.7505 13.7096C14.7866 13.6938 14.819 13.6707 14.8459 13.6417C14.8727 13.6127 14.8934 13.5784 14.9066 13.5409C14.9198 13.5035 14.9253 13.4637 14.9227 13.424C14.9163 13.3494 14.8823 13.28 14.8276 13.2298C14.7728 13.1795 14.7013 13.1521 14.6274 13.1531H13.7773C13.7301 13.153 13.6837 13.1648 13.6421 13.1873C13.6005 13.2098 13.565 13.2424 13.5389 13.2822L13.2045 13.7899L12.2975 12.4117C12.2627 12.3589 12.2118 12.319 12.1525 12.2983C12.0932 12.2776 12.0288 12.2772 11.9692 12.2971C11.9063 12.3189 11.8524 12.3616 11.8164 12.4183L11.3332 13.1531H9.75136C9.72076 13.1531 9.69093 13.1433 9.66626 13.125C9.64159 13.1066 9.62337 13.0807 9.61426 13.0511C9.52789 12.7793 9.48322 12.4957 9.48182 12.2101C9.48248 11.6139 9.71651 11.0424 10.1326 10.6208C10.5486 10.1993 11.1127 9.96218 11.7011 9.96151C12.4403 9.96151 13.0875 10.2836 13.4909 10.8279C13.8943 10.2836 14.5415 9.96151 15.2807 9.96151C15.8691 9.96218 16.4332 10.1993 16.8492 10.6208C17.2653 11.0424 17.4993 11.6139 17.5 12.2101Z" :fill="task.completed ? '#999' : task.overdue ? '#666' : '#004879'"/>
                      </svg>
                      <span :class="['tasks-tab__patient-name', { 'tasks-tab__text--muted': task.completed }]">{{ getPatientName(task) }}</span>
                    </template>
                  </div>
                  <div :class="['tasks-tab__task-desc', { 'tasks-tab__text--muted': task.completed }]">
                    {{ task.description }}
                  </div>
                </div>
              </div>

              <!-- Due date -->
              <div :class="['tasks-tab__due-date', { 'tasks-tab__text--muted': task.completed }]">
                <svg width="17" height="19" viewBox="0 0 17 19" fill="none" xmlns="http://www.w3.org/2000/svg" class="tasks-tab__calendar-icon">
                  <path d="M15 1.66667H14.1667V0H12.5V1.66667H4.16667V0H2.5V1.66667H1.66667C0.75 1.66667 0 2.41667 0 3.33333V16.6667C0 17.5833 0.75 18.3333 1.66667 18.3333H15C15.9167 18.3333 16.6667 17.5833 16.6667 16.6667V3.33333C16.6667 2.41667 15.9167 1.66667 15 1.66667ZM15 16.6667H1.66667V7.5H15V16.6667ZM15 5.83333H1.66667V3.33333H15V5.83333Z" :fill="task.completed ? '#999' : task.overdue ? (isRowHovered(task, index) ? '#D10000' : '#A60B0B') : '#C4C4C4'"/>
                </svg>
                <span class="tasks-tab__due-date-text">{{ task.dueDate }}</span>
              </div>

              <!-- Action buttons (visible on hover for incomplete tasks) -->
              <div :class="['tasks-tab__actions', { 'tasks-tab__actions--visible': hoveredId === getTaskRowKey(task, index) && !task.completed }]">
                <button class="tasks-tab__action-btn" @click="openEdit(task)">
                  <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                    <path d="M11.2593 3C11.3558 3 11.4483 3.03665 11.5165 3.10188L13.5735 5.06948C13.7155 5.20531 13.7155 5.42554 13.5735 5.56138L6.88814 11.9561C6.84245 11.9998 6.78542 12.031 6.72291 12.0467L3.9386 12.7423C3.81369 12.7735 3.68085 12.739 3.58957 12.6517C3.49828 12.5644 3.46224 12.4374 3.49487 12.3179L4.22214 9.65463C4.23847 9.59483 4.27116 9.54028 4.31684 9.49658L11.0022 3.10188C11.0704 3.03665 11.1629 3 11.2593 3Z" fill="#374957"/>
                    <path d="M3.54545 13.9565C3.24421 13.9565 3 14.1901 3 14.4783C3 14.7664 3.24421 15 3.54545 15H14.4545C14.7558 15 15 14.7664 15 14.4783C15 14.1901 14.7558 13.9565 14.4545 13.9565H3.54545Z" fill="#374957"/>
                  </svg>
                  <span>Edit</span>
                </button>
                <button class="tasks-tab__action-btn tasks-tab__action-btn--delete" @click="openDelete(task)">
                  <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                    <path d="M9.02393 4.41544C11.01 4.41544 12.9961 4.41544 14.9822 4.41544C15.079 4.41544 15.1759 4.41544 15.2728 4.41544C15.6926 4.44722 16.0156 4.73326 15.9994 5.09875C15.9994 5.43246 15.6765 5.73439 15.2728 5.75028C14.9176 5.76617 14.5462 5.76617 14.191 5.75028C13.9326 5.75028 13.868 5.84563 13.8519 6.0681C13.755 7.40295 13.6581 8.72191 13.5612 10.0409C13.4482 11.6141 13.319 13.1873 13.1898 14.7605C13.1253 15.5074 12.5601 16 11.8012 16C9.84743 16 7.89364 16 5.93986 16C5.11636 16 4.58351 15.4756 4.51893 14.6652C4.32516 12.0908 4.1314 9.50057 3.93764 6.92622C3.92149 6.62429 3.87305 6.32236 3.87305 6.00454C3.87305 5.81385 3.79231 5.73439 3.59855 5.75028C3.40479 5.75028 3.21102 5.75028 3.01726 5.75028C2.37138 5.75028 2 5.51192 2 5.09875C2 4.66969 2.37138 4.41544 3.01726 4.41544C4.47049 4.41544 5.90757 4.41544 7.36079 4.41544C7.90979 4.41544 8.45879 4.41544 9.00778 4.41544H9.02393ZM7.94208 10.2474C7.94208 9.58002 7.94208 8.92849 7.94208 8.26107C7.94208 7.8479 7.79676 7.65721 7.48997 7.65721C7.19932 7.65721 7.03785 7.8479 7.03785 8.24518C7.03785 8.86493 7.03785 9.46879 7.03785 10.0885C7.03785 10.7877 7.03785 11.5028 7.03785 12.202C7.03785 12.5993 7.29621 12.8377 7.603 12.7423C7.86135 12.6629 7.94208 12.4563 7.94208 12.2179C7.94208 11.5505 7.94208 10.899 7.94208 10.2316V10.2474ZM10.6709 10.1998C10.6709 9.53235 10.6709 8.88082 10.6709 8.21339C10.6709 7.83201 10.5095 7.64132 10.235 7.64132C9.94431 7.64132 9.78284 7.83201 9.78284 8.1975C9.78284 9.51646 9.78284 10.8354 9.78284 12.1544C9.78284 12.5516 9.94431 12.7423 10.2511 12.7423C10.5417 12.7423 10.6871 12.5516 10.6871 12.1703C10.6871 11.5028 10.6871 10.8513 10.6871 10.1839L10.6709 10.1998Z" fill="#D10000"/>
                    <path d="M9.04008 2C9.52449 2 10.025 2 10.5095 2C10.9454 2 11.2038 2.25426 11.2038 2.63564C11.2038 3.03292 10.9777 3.28717 10.5417 3.30306C9.54063 3.30306 8.52337 3.30306 7.52226 3.30306C7.11859 3.30306 6.8118 3.00113 6.8118 2.65153C6.8118 2.28604 7.11859 2 7.55456 2C8.03897 2 8.53952 2 9.02393 2H9.04008Z" fill="#D10000"/>
                  </svg>
                  <span>Delete</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Pagination -->
        <div v-if="total > 0" class="tasks-tab__pagination">
          <Pagination
            :total_items="total"
            :current_page="page"
            :per_page="perPage"
            :per_page_options="[10, 20, 50]"
            item_label="Tasks"
            @update:current_page="changePage"
            @update:per_page="changePerPage"
          />
        </div>
        </div>
      </template>
    </div>

    <!-- Edit Task Dialog -->
    <ScheduleDetail
      v-if="editDialogVisible"
      :visible="editDialogVisible"
      active-tab="task"
      :hide-switch-tab="true"
      @close="closeEdit"
      @updateTask="onTaskUpdated"
      @createTask="onTaskUpdated"
    />

    <!-- Delete Task Dialog -->
    <DeleteTaskDialog
      v-if="deleteDialogVisible"
      :visible="deleteDialogVisible"
      @close="deleteDialogVisible = false"
      @delete="confirmDelete"
    />
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import moment from 'moment-timezone';
import Loading from '@/NewLibrary/Loading/Loading.vue';
import ScheduleDetail from '@/views/my-calendar/sections/schedule/ScheduleDetail.vue';
import DeleteTaskDialog from '@/views/my-calendar/sections/task/DeleteTaskDialog.vue';

export default {
  name: 'TasksTab',
  components: { Loading, ScheduleDetail, DeleteTaskDialog },
  data() {
    return {
      subTabs: [
        { value: 'all', label: 'All' },
        { value: 'created_by_me', label: 'Created by me' },
      ],
      initLoading: false,
      hoveredId: null,
      editDialogVisible: false,
      deleteDialogVisible: false,
      taskToDelete: null,
    };
  },
  async created() {
    // Ensure calendar store is initialized before tasks are displayed,
    // so formatDueDate/isOverdue use the correct timezone (not default 'UTC')
    if (!this.$store.state.calendar.creatorCalendarId) {
      this.initLoading = true;
      try {
        const userInfo = this.$store.state.login.userInfo;
        await this.$store.dispatch('calendar/getOrCreateCalendar', {
          calendar_owner_id: userInfo.customer_id,
          role: userInfo.user_roles?.includes('clinic') ? 'clinicadmin' : 'provider',
          practice_id: userInfo.clinic_id
        });
        // Load patient list and re-fetch tasks in parallel, await both so
        // patient names are ready before the loading spinner disappears
        await Promise.all([
          this.$store.dispatch('calendar/fetchClinic', { clinicId: userInfo.clinic_id }),
          this.$store.dispatch('notifications/fetchTasks', { silent: true }),
        ]);
      } finally {
        this.initLoading = false;
      }
    }
  },
  computed: {
    ...mapState('notifications', {
      items: state => state.tasks.items,
      loading: state => state.tasks.loading,
      error: state => state.tasks.error,
      filter: state => state.tasks.filter,
      page: state => state.tasks.page,
      perPage: state => state.tasks.perPage,
      total: state => state.tasks.total,
    }),
    ...mapGetters('calendar', ['formattedPatientsList']),
    patientNameMap() {
      const map = {};
      for (const p of this.formattedPatientsList) {
        if (p.calendar_id) map[p.calendar_id] = p.name;
      }
      return map;
    },
  },
  methods: {
    isRowHovered(task, index) {
      return this.hoveredId === this.getTaskRowKey(task, index);
    },
    getPatientName(task) {
      if (!task.calendar_id) return '';
      return this.patientNameMap[task.calendar_id] || '';
    },
    getTaskKey(task) {
      return task?.task_id ?? task?.id ?? null;
    },
    getTaskRowKey(task, index) {
      const baseKey = this.getTaskKey(task);
      const occurrenceKey = this.getOccurrenceSourceDate(task) ?? task?.created_at ?? index;
      return `${baseKey ?? 'task'}::${occurrenceKey}`;
    },
    getOccurrenceSourceDate(task) {
      return task?.due_date ?? task?.start_date ?? null;
    },
    getCompletionOccurrenceDate(task) {
      return task?.start_date ?? task?.due_date ?? null;
    },
    changeFilter(filter) {
      this.$store.commit('notifications/SET_TASKS_FILTER', filter);
      this.$store.commit('notifications/SET_TASKS_PAGE', 1);
      this.$store.dispatch('notifications/fetchTasks');
    },
    changePage(page) {
      this.$store.commit('notifications/SET_TASKS_PAGE', page);
      this.$store.dispatch('notifications/fetchTasks');
    },
    changePerPage(val) {
      this.$store.commit('notifications/SET_TASKS_PER_PAGE', val);
      this.$store.commit('notifications/SET_TASKS_PAGE', 1);
      this.$store.dispatch('notifications/fetchTasks');
    },
    retry() {
      this.$store.dispatch('notifications/fetchTasks');
    },
    async toggleComplete(task) {
      try {
        const isRecurring = Boolean(task.rrule);
        const occurrenceSourceDate = this.getCompletionOccurrenceDate(task);
        const occurrenceDate = isRecurring && occurrenceSourceDate
          ? moment.utc(occurrenceSourceDate).toISOString()
          : null;
        const payload = isRecurring
          ? { id: task.task_id, occurrenceDate }
          : { id: task.task_id };
        const action = task.completed
          ? 'calendar/unCompleteTask'
          : 'calendar/completeTask';
        await this.$store.dispatch(action, payload);
        await this.$store.dispatch('notifications/fetchTasks');
      } catch (e) {
        console.error('toggleComplete error:', e);
      }
    },
    openEdit(task) {
      const occurrenceSourceDate = this.getOccurrenceSourceDate(task);
      const timezone = this.$store.getters['calendar/calendarTimezone'];
      const selectedDate = occurrenceSourceDate
        ? moment.utc(occurrenceSourceDate).tz(timezone).format('YYYY-MM-DD')
        : '';
      const selectedDueTime = occurrenceSourceDate
        ? moment.utc(occurrenceSourceDate).tz(timezone).format('hh:mma')
        : '';

      this.$store.dispatch('calendar/setCurrentTaskInfo', {
        task_id: task.task_id,
        calendar_id: task.calendar_id,
        title: task.title,
        description: task.description,
        priority: task.priority,
        category: task.category,
        start_date: task.start_date,
        due_date: task.due_date,
        rrule: task.rrule,
        is_completed: task.is_completed,
        selectedDate,
        selectedDueTime,
        mode: 'edit',
      });
      this.editDialogVisible = true;
    },
    closeEdit() {
      this.editDialogVisible = false;
      this.$store.dispatch('calendar/setCurrentTaskInfo', null);
    },
    onTaskUpdated() {
      this.closeEdit();
      this.$store.dispatch('notifications/fetchTasks');
    },
    openDelete(task) {
      this.taskToDelete = task;
      this.deleteDialogVisible = true;
    },
    async confirmDelete() {
      this.deleteDialogVisible = false;
      if (!this.taskToDelete) return;
      try {
        await this.$store.dispatch('calendar/deleteTask', this.taskToDelete.task_id);
        this.$store.dispatch('notifications/fetchTasks');
      } catch (e) {
        console.error('deleteTask error:', e);
      }
      this.taskToDelete = null;
    },
  },
};
</script>

<style scoped>
.tasks-tab {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  gap: 24px;
}

/* Bordered container — dashed border wrapping sub-tabs, table, and pagination */
.tasks-tab__bordered-container {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  border: 1px dashed #C4C4C4;
  border-radius: 8px;
  background: #FFFFFF;
  padding-top: 16px;
  gap: 24px;
}

/* Sub-tabs — px-48 from Figma tab group */
.tasks-tab__sub-tabs-wrapper {
  padding: 0 48px;
}

/* Loading / Empty / Error */
.tasks-tab__loading,
.tasks-tab__empty {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  padding: 40px 0;
  color: #999;
  font-family: Inter, sans-serif;
  font-size: 14px;
}

.tasks-tab__error-box {
  display: flex;
  align-items: center;
  gap: 8px;
  background: #FAEAEA;
  border: 1px solid #f5c6c6;
  border-radius: 8px;
  padding: 12px 16px;
  color: #D10000;
  font-family: Inter, sans-serif;
  font-size: 14px;
}

.tasks-tab__retry {
  margin-left: auto;
  background: none;
  border: 1px solid #D10000;
  border-radius: 4px;
  color: #D10000;
  padding: 4px 12px;
  font-size: 12px;
  cursor: pointer;
}

/* Content area — "today task" container from Figma: px-24, pb-24, gap-10 */
.tasks-tab__content-area {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 0 24px 24px 24px;
  flex: 1;
  min-height: 0;
}

/* Title + Content wrapper — wraps header row + task rows */
.tasks-tab__title-content {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
}

/* Header row — bg: #F9FBFD, h: 24px */
.tasks-tab__header-row {
  display: flex;
  align-items: center;
  padding: 0 24px 0 12px;
  height: 32px;
  background: #F9FBFD;
  border-radius: 4px;
  flex-shrink: 0;
}

.tasks-tab__header-left {
  display: flex;
  gap: 20px;
  align-items: center;
  flex: 1;
  min-width: 0;
}

.tasks-tab__header-checkbox-spacer {
  width: 24px;
  height: 24px;
  flex-shrink: 0;
  opacity: 0;
}

.tasks-tab__header-title-desc {
  display: flex;
  gap: 20px;
  align-items: center;
  flex: 1;
  min-width: 0;
}

.tasks-tab__header-title {
  width: 200px;
  font-family: Inter, sans-serif;
  font-weight: 600;
  font-size: 12px;
  line-height: 16px;
  letter-spacing: 0.4px;
  color: #999;
  text-transform: uppercase;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.tasks-tab__header-patient {
  width: 140px;
  flex-shrink: 0;
  font-family: Inter, sans-serif;
  font-weight: 600;
  font-size: 12px;
  line-height: 16px;
  letter-spacing: 0.4px;
  color: #999;
  text-transform: uppercase;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.tasks-tab__header-desc {
  flex: 1;
  min-width: 0;
  font-family: Inter, sans-serif;
  font-weight: 600;
  font-size: 12px;
  line-height: 16px;
  letter-spacing: 0.4px;
  color: #999;
  text-transform: uppercase;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.tasks-tab__header-date {
  font-family: Inter, sans-serif;
  font-weight: 600;
  font-size: 12px;
  line-height: 16px;
  letter-spacing: 0.4px;
  color: #999;
  text-transform: uppercase;
  white-space: nowrap;
  width: 280px;
  padding-left: 30px; /* align with date text: 18px icon + 12px gap */
}

/* Task rows container */
.tasks-tab__rows-container {
  display: flex;
  flex-direction: column;
  flex: 1;
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: #c1c1c1 transparent;
}

.tasks-tab__rows-container::-webkit-scrollbar {
  width: 4px;
}

.tasks-tab__rows-container::-webkit-scrollbar-track {
  background: transparent;
}

.tasks-tab__rows-container::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 2px;
}

.tasks-tab__rows-container::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

/* Task row — py-12, pl-12, pr-24 */
.tasks-tab__row {
  display: flex;
  align-items: center;
  padding: 12px 24px 12px 12px;
  background: white;
  border-radius: 4px;
  flex-shrink: 0;
  transition: background 0.15s;
}

.tasks-tab__row--completed {
  background: #F7F7F7;
}

.tasks-tab__row--overdue {
  background: white;
}

.tasks-tab__row--overdue .tasks-tab__task-title,
.tasks-tab__row--overdue .tasks-tab__task-desc,
.tasks-tab__row--overdue .tasks-tab__due-date-text {
  color: #A60B0B;
}

/* Normal row hover — cool white background */
.tasks-tab__row:not(.tasks-tab__row--completed):not(.tasks-tab__row--overdue):hover {
  background: #F9FBFD;
}

/* Overdue row hover — light red tint */
.tasks-tab__row--overdue:hover {
  background: rgba(250, 234, 234, 0.5);
}

.tasks-tab__row--overdue:hover .tasks-tab__task-title,
.tasks-tab__row--overdue:hover .tasks-tab__task-desc,
.tasks-tab__row--overdue:hover .tasks-tab__due-date-text {
  color: #D10000;
}

.tasks-tab__row--overdue:hover .tasks-tab__circle {
  border-color: #D10000;
}

.tasks-tab__row-left {
  display: flex;
  gap: 20px;
  align-items: center;
  flex: 1;
  min-width: 0;
}

/* Circle checkbox */
.tasks-tab__circle {
  width: 24px;
  height: 24px;
  border: 1px solid #004879;
  border-radius: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: white;
  cursor: pointer;
}

.tasks-tab__circle--checked {
  border-color: #50C5CE;
  background: transparent;
}

.tasks-tab__row--overdue .tasks-tab__circle {
  border-color: #A60B0B;
}

/* Title + Description in each row */
.tasks-tab__title-desc-row {
  display: flex;
  gap: 20px;
  align-items: center;
  height: 34px;
  flex: 1;
  min-width: 0;
}

.tasks-tab__task-title {
  width: 200px;
  font-family: Inter, sans-serif;
  font-weight: 500;
  font-size: 14px;
  line-height: 16px;
  letter-spacing: 0.4px;
  color: #333;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.tasks-tab__patient-cell {
  width: 140px;
  flex-shrink: 0;
  display: flex;
  gap: 4px;
  align-items: center;
  min-width: 0;
}

.tasks-tab__patient-icon {
  flex-shrink: 0;
}

.tasks-tab__patient-name {
  font-family: Inter, sans-serif;
  font-weight: 400;
  font-size: 12px;
  line-height: normal;
  letter-spacing: 0.4px;
  color: #004879;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.tasks-tab__row--overdue .tasks-tab__patient-name {
  color: #333;
}

.tasks-tab__task-desc {
  flex: 1;
  min-width: 0;
  font-family: Inter, sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: normal;
  letter-spacing: 0.4px;
  color: #666;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.tasks-tab__text--muted {
  color: #999 !important;
}

/* Due date */
.tasks-tab__due-date {
  display: flex;
  gap: 12px;
  align-items: center;
  flex-shrink: 0;
  width: 280px;
}

.tasks-tab__calendar-icon {
  flex-shrink: 0;
}

.tasks-tab__due-date-text {
  font-family: Inter, sans-serif;
  font-weight: 500;
  font-size: 14px;
  line-height: 16px;
  letter-spacing: 0.4px;
  color: #666;
  white-space: nowrap;
}

.tasks-tab__row--completed .tasks-tab__due-date-text {
  color: #999;
}

/* Action buttons */
.tasks-tab__header-actions-spacer {
  width: 120px;
  flex-shrink: 0;
}

.tasks-tab__actions {
  display: flex;
  gap: 16px;
  align-items: center;
  width: 120px;
  flex-shrink: 0;
  justify-content: flex-end;
  opacity: 0;
  transition: opacity 0.15s;
}

.tasks-tab__actions--visible {
  opacity: 1;
}

.tasks-tab__action-btn {
  display: flex;
  gap: 4px;
  align-items: center;
  background: none;
  border: none;
  font-family: Lato, sans-serif;
  font-size: 14px;
  color: #666;
  cursor: pointer;
  padding: 0;
}

.tasks-tab__action-btn--delete {
  color: #D10000;
}

/* Pagination */
.tasks-tab__pagination {
  flex-shrink: 0;
}
</style>
